import { useQuery } from '@tanstack/react-query';
import type { UseQueryOptions } from '@tanstack/react-query';
import { ApplicationsService, HealthService } from '../api/user-portal-service';
import { APP_CONFIG } from '../common/constants';
import { queryKeys } from '../common/queryClient';
import type { UserApplication } from '../types/api.types';

export interface UserApplicationsResult {
  items: UserApplication[];
  total: number;
}

/**
 * Hook to fetch user applications
 */
export function useApplications(
  options?: Omit<
    UseQueryOptions<UserApplicationsResult, Error>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: queryKeys.applications.list(),
    queryFn: async (): Promise<UserApplicationsResult> => {
      const response = await ApplicationsService.getUserApplications();
      const allItems = response.items ?? [];
      const items = allItems.filter(app => app.app_name !== APP_CONFIG.name);
      const hiddenCount = allItems.length - items.length;

      // If the portal's own entry falls past a truncated page, it can't be
      // seen here to subtract, so total stays one too high in that one case.
      return { items, total: response.total - hiddenCount };
    },
    ...options,
  });
}

/**
 * Hook to check API health
 */
export function useHealthCheck(
  options?: Omit<
    UseQueryOptions<Record<string, unknown>, Error>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: ['health'],
    queryFn: () => HealthService.getHealth(),
    staleTime: 30000, // 30 seconds
    ...options,
  });
}
